import is from '@sindresorhus/is';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { UseAudioTranscriptionReturn } from '~/shared/audio-transcription';
import { useAudioTranscription } from '~/shared/audio-transcription';
import { useApplicationContext } from '~/shared/context/use-application-context';

export type UseVoiceTranscriptionReturn = Pick<
    UseAudioTranscriptionReturn,
    'isRecording' | 'isTranscribing' | 'stopRecording'
> & {
    readonly getAmplitude: () => number;
    readonly inputFadingIn: boolean;
    readonly overlayFadingOut: boolean;
    readonly recordingSeconds: number;
    readonly showOverlay: boolean;
    readonly toggleRecording: () => void;
};

export const useVoiceTranscription = (onTranscribedText: (text: string) => void): UseVoiceTranscriptionReturn => {
    const { realtimeEventListenerBuilder } = useApplicationContext();
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [overlayFadingOut, setOverlayFadingOut] = useState(false);
    const [inputFadingIn, setInputFadingIn] = useState(false);
    const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const analyserStreamRef = useRef<MediaStream | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const analyserDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
    const isTogglingRef = useRef(false);

    const stopAnalyser = useCallback(() => {
        const stream = analyserStreamRef.current;
        if (!is.null(stream)) {
            for (const track of stream.getTracks()) {
                track.stop();
            }
            analyserStreamRef.current = null;
        }
        const audioCtx = audioCtxRef.current;
        if (!is.null(audioCtx)) {
            void audioCtx.close();
            audioCtxRef.current = null;
        }
        analyserRef.current = null;
        analyserDataRef.current = null;
    }, []);

    const startAnalyser = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            analyserStreamRef.current = stream;

            const audioCtx = new AudioContext();
            audioCtxRef.current = audioCtx;
            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 512;
            analyser.smoothingTimeConstant = 0.15;
            analyser.minDecibels = -90;
            analyser.maxDecibels = -10;
            source.connect(analyser);
            analyserRef.current = analyser;
            analyserDataRef.current = new Uint8Array(analyser.frequencyBinCount);
        } catch {
            stopAnalyser();
            // Mic permission denied - waveform stays flat.
        }
    }, [stopAnalyser]);

    const getAmplitude = useCallback((): number => {
        const analyser = analyserRef.current;
        const data = analyserDataRef.current;
        if (is.null(analyser) || is.null(data)) {
            return 0;
        }
        analyser.getByteFrequencyData(data);
        let sum = 0;
        const len = data.length;
        for (let i = 0; i < len; i++) {
            sum += data[i] ?? 0;
        }
        const avg = sum / len / 255;
        return Math.min(1, avg ** 0.35 * 1.8);
    }, []);

    const handleTranscriptionDone = useCallback(() => {
        stopAnalyser();
        setOverlayFadingOut(true);
        setInputFadingIn(true);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => setInputFadingIn(false));
        });
        setTimeout(() => setOverlayFadingOut(false), 400);
    }, [stopAnalyser]);

    const { isRecording, isTranscribing, startRecording, stopRecording } = useAudioTranscription({
        onChunk: onTranscribedText,
        onComplete: handleTranscriptionDone,
        onError: handleTranscriptionDone,
        realtimeEventListenerBuilder,
    });

    useEffect(() => {
        if (isRecording) {
            setRecordingSeconds(0);
            recordingTimerRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
        } else if (!is.null(recordingTimerRef.current)) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }

        return () => {
            if (!is.null(recordingTimerRef.current)) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
        };
    }, [isRecording]);

    useEffect(() => {
        return () => stopAnalyser();
    }, [stopAnalyser]);

    const toggleRecording = useCallback(async () => {
        if (isTogglingRef.current) return;
        isTogglingRef.current = true;
        try {
            if (isRecording) {
                await stopRecording();
            } else {
                await startAnalyser();
                await startRecording();
            }
        } catch (error) {
            stopAnalyser();
            throw error;
        } finally {
            isTogglingRef.current = false;
        }
    }, [isRecording, startAnalyser, startRecording, stopAnalyser, stopRecording]);

    return {
        getAmplitude,
        inputFadingIn,
        isRecording,
        isTranscribing,
        overlayFadingOut,
        recordingSeconds,
        showOverlay: isRecording || isTranscribing || overlayFadingOut,
        stopRecording,
        toggleRecording,
    };
};
